import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
 Star,
 Search,
 CheckCircle2,
 EyeOff,
 Trash2,
 Loader2,
 ListChecks,
 MessageSquareWarning,
} from 'lucide-react';
import {
 useAdminReviews,
 useAdminReviewStatistics,
 useApproveAdminReview,
 useHideAdminReview,
 useDeleteAdminReview,
 useBulkApproveAdminReviews,
} from '../../services/adminReviewService';
import { AdminPageHeader, AdminGuideCallout, AdminEmptyState } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import { useToastContext } from '../../contexts/ToastContext';
import { formatDate } from '../../utils/dateUtils';
import Button from '../../components/shared/Button';

const STATUS_PILLS = [
 { value: 'pending', label: 'Pending' },
 { value: 'approved', label: 'Published' },
 { value: 'all', label: 'All' },
];

const RATING_PILLS = [
 { value: '', label: 'Any ★' },
 { value: '5', label: '5★' },
 { value: '4', label: '4★' },
 { value: '3', label: '3★' },
 { value: '2', label: '2★' },
 { value: '1', label: '1★' },
];

const Stars = ({ rating }) => (
 <div className="flex items-center gap-0.5">
 {[1, 2, 3, 4, 5].map((i) => (
 <Star
 key={i}
 className={`w-3.5 h-3.5 ${ i <= Number(rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300' }`}
 />
 ))}
 </div>
);

const AdminReviews = () => {
 const { success, error: showError } = useToastContext();
 const [status, setStatus] = useState('pending');
 const [rating, setRating] = useState('');
 const [search, setSearch] = useState('');
 const [page, setPage] = useState(1);
 const [selected, setSelected] = useState([]);

 const params = useMemo(
 () => ({
 status,
 rating: rating || undefined,
 search: search || undefined,
 page,
 per_page: 20,
 sort: 'newest',
 }),
 [status, rating, search, page]
 );

 const { data, isLoading, error } = useAdminReviews(params);
 const { data: statsData } = useAdminReviewStatistics();
 const stats = statsData?.data;

 const approveMutation = useApproveAdminReview({
 onSuccess: () => success('Review published'),
 onError: (err) => showError(err?.response?.data?.message || 'Approve failed'),
 });
 const hideMutation = useHideAdminReview({
 onSuccess: () => success('Review hidden'),
 onError: (err) => showError(err?.response?.data?.message || 'Hide failed'),
 });
 const deleteMutation = useDeleteAdminReview({
 onSuccess: () => success('Review deleted'),
 onError: (err) => showError(err?.response?.data?.message || 'Delete failed'),
 });
 const bulkApproveMutation = useBulkApproveAdminReviews({
 onSuccess: (res) => {
 success(res?.message || 'Reviews approved');
 setSelected([]);
 },
 onError: (err) => showError(err?.response?.data?.message || 'Bulk approve failed'),
 });

 const paginator = data?.data;
 const reviews = paginator?.data || [];

 const toggleSelect = (id) => {
 setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
 };

 const toggleSelectAllPending = () => {
 const pendingIds = reviews.filter((r) => !r.is_approved).map((r) => r.id);
 if (pendingIds.length === 0) return;
 const allSelected = pendingIds.every((id) => selected.includes(id));
 setSelected(allSelected ? selected.filter((id) => !pendingIds.includes(id)) : [
 ...new Set([...selected, ...pendingIds]),
 ]);
 };

 const handleDelete = (review) => {
 if (
 !window.confirm(
 `Permanently delete review from ${review.client?.name || 'client'}? This cannot be undone.`
 )
 ) {
 return;
 }
 deleteMutation.mutate(review.id);
 };

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Reviews moderation"
 subtitle="Approve, hide, or remove client reviews before or after they appear on vendor profiles."
 actions={
 selected.length > 0 ? (
 <Button
 className="rounded-md whitespace-nowrap"
 onClick={() => bulkApproveMutation.mutate(selected)}
 disabled={bulkApproveMutation.isPending}
 >
 {bulkApproveMutation.isPending ? (
 <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
 ) : (
 <CheckCircle2 className="w-4 h-4 mr-1.5" />
 )}
 Approve selected ({selected.length})
 </Button>
 ) : null
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.reviews.title}
 steps={ADMIN_PAGE_GUIDES.reviews.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
 {[
 { label: 'Pending', value: stats?.pending ?? '—', tone: 'amber' },
 { label: 'Published', value: stats?.approved ?? '—', tone: 'emerald' },
 { label: 'Total', value: stats?.total ?? '—', tone: 'slate' },
 {
 label: 'Avg rating',
 value: stats?.average_rating != null ? Number(stats.average_rating).toFixed(1) : '—',
 tone: 'sky',
 },
 ].map((card) => (
 <div
 key={card.label}
 className="product-panel p-4 shadow-sm"
 >
 <p className="text-xs font-medium text-ink-tertiary mb-1">{card.label}</p>
 <p className="text-2xl font-bold tabular-nums text-ink">{card.value}</p>
 </div>
 ))}
 </div>

 <div className="product-panel p-4 sm:p-5 space-y-4">
 <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 >
 {STATUS_PILLS.map((pill) => {
 const active = status === pill.value;
 const count =
 pill.value === 'pending'
 ? stats?.pending
 : pill.value === 'approved'
 ? stats?.approved
 : stats?.total;
 return (
 <button
 key={pill.value}
 type="button"
 onClick={() => {
 setStatus(pill.value);
 setPage(1);
 setSelected([]);
 }}
 className={`px-3 py-1.5 text-sm font-medium !rounded-lg transition-colors ${ active ? 'bg-surface text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900' }`}
 >
 {pill.label}
 {count != null && (
 <span className="ml-1.5 tabular-nums text-xs text-gray-400">{count}</span>
 )}
 </button>
 );
 })}
 </div>

 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 >
 {RATING_PILLS.map((pill) => {
 const active = rating === pill.value;
 return (
 <button
 key={pill.value || 'any'}
 type="button"
 onClick={() => {
 setRating(pill.value);
 setPage(1);
 }}
 className={`px-2.5 py-1.5 text-xs font-medium !rounded-lg transition-colors ${ active ? 'bg-surface text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900' }`}
 >
 {pill.label}
 </button>
 );
 })}
 </div>
 </div>

 <div className="relative max-w-md">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
 <input
 type="text"
 value={search}
 onChange={(e) => {
 setSearch(e.target.value);
 setPage(1);
 }}
 placeholder="Search client, vendor, title, booking…"
 className="w-full pl-10 pr-3 py-1.5 text-xs rounded-md border border-border bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-ink"
 />
 </div>
 </div>

 {isLoading ? (
 <div className="product-panel py-16 flex items-center justify-center gap-2 text-sm text-gray-500">
 <Loader2 className="w-5 h-5 animate-spin text-ink" />
 Loading reviews…
 </div>
 ) : error ? (
 <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
 Could not load reviews.
 </div>
 ) : reviews.length === 0 ? (
 <AdminEmptyState
 icon={MessageSquareWarning}
 title={status === 'pending' ? 'No pending reviews' : 'No reviews found'}
 description={
 status === 'pending'
 ? 'New client reviews will appear here until you publish them.'
 : 'Try another status, rating, or search term.'
 }
 />
 ) : (
 <div className="space-y-3">
 {status === 'pending' && reviews.some((r) => !r.is_approved) && (
 <div className="flex items-center justify-between px-1">
 <button
 type="button"
 onClick={toggleSelectAllPending}
 className="text-xs font-medium text-ink hover:text-ink"
 >
 {reviews.filter((r) => !r.is_approved).every((r) => selected.includes(r.id))
 ? 'Clear selection'
 : 'Select all pending on this page'}
 </button>
 </div>
 )}

 {reviews.map((review) => {
 const vendorUserId = review.vendor?.user_id;
 return (
 <article
 key={review.id}
 className="product-panel p-4 sm:p-5 shadow-sm"
 >
 <div className="flex items-start gap-3">
 {!review.is_approved && (
 <input
 type="checkbox"
 checked={selected.includes(review.id)}
 onChange={() => toggleSelect(review.id)}
 className="mt-1.5 rounded border-gray-300 text-ink focus:ring-ink"
 aria-label="Select review"
 />
 )}
 <div className="min-w-0 flex-1">
 <div className="flex flex-wrap items-center gap-2 mb-2">
 <Stars rating={review.rating} />
 <span
 className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${ review.is_approved ? 'bg-emerald-50 text-emerald-800 border-emerald-200/80' : 'bg-amber-50 text-amber-800 border-amber-200/80' }`}
 >
 {review.is_approved ? 'Published' : 'Pending'}
 </span>
 {review.is_verified && (
 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-muted text-ink border border-border/80">
 Verified booking
 </span>
 )}
 </div>

 {review.title && (
 <h3 className="text-sm font-semibold text-ink mb-1">
 {review.title}
 </h3>
 )}
 {review.comment && (
 <p className="text-sm text-ink-secondary leading-relaxed mb-3">
 {review.comment}
 </p>
 )}

 <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-tertiary mb-4">
 <span>
 Client:{' '}
 <span className="font-medium text-ink-secondary">
 {review.client?.name || '—'}
 </span>
 </span>
 <span>
 Vendor:{' '}
 {vendorUserId ? (
 <Link
 to={`/admin/vendors/${vendorUserId}`}
 className="font-medium text-ink hover:underline"
 >
 {review.vendor?.business_name || 'Vendor'}
 </Link>
 ) : (
 <span className="font-medium text-ink-secondary">
 {review.vendor?.business_name || '—'}
 </span>
 )}
 </span>
 {review.booking?.booking_reference && (
 <span>
 Booking:{' '}
 <Link
 to={`/admin/bookings/${review.booking_id || review.booking.id}`}
 className="font-medium text-ink hover:underline"
 >
 {review.booking.booking_reference}
 </Link>
 </span>
 )}
 <span>{review.created_at ? formatDate(review.created_at, 'short') : ''}</span>
 </div>

 <div className="flex flex-wrap gap-2">
 {!review.is_approved ? (
 <Button
 size="sm"
 className="rounded-md"
 onClick={() => approveMutation.mutate(review.id)}
 disabled={approveMutation.isPending}
 >
 <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
 Publish
 </Button>
 ) : (
 <Button
 size="sm"
 variant="outline"
 className="rounded-md"
 onClick={() => hideMutation.mutate(review.id)}
 disabled={hideMutation.isPending}
 >
 <EyeOff className="w-3.5 h-3.5 mr-1.5" />
 Hide
 </Button>
 )}
 <Button
 size="sm"
 variant="outline"
 className="rounded-md text-red-600 border-red-200 hover:bg-red-50"
 onClick={() => handleDelete(review)}
 disabled={deleteMutation.isPending}
 >
 <Trash2 className="w-3.5 h-3.5 mr-1.5" />
 Delete
 </Button>
 </div>
 </div>
 </div>
 </article>
 );
 })}

 {paginator?.last_page > 1 && (
 <div className="flex items-center justify-between pt-2">
 <p className="text-xs text-gray-500">
 Page {paginator.current_page} of {paginator.last_page}
 </p>
 <div className="flex gap-2">
 <button
 type="button"
 disabled={page <= 1}
 onClick={() => setPage((p) => Math.max(1, p - 1))}
 className="px-3 py-1.5 text-xs font-medium rounded-md border border-border disabled:opacity-40"
 >
 Previous
 </button>
 <button
 type="button"
 disabled={page >= paginator.last_page}
 onClick={() => setPage((p) => p + 1)}
 className="px-3 py-1.5 text-xs font-medium rounded-md border border-border disabled:opacity-40"
 >
 Next
 </button>
 </div>
 </div>
 )}
 </div>
 )}
 </div>
 );
};

export default AdminReviews;
